<script setup>
// SSR-safe Teleport: при включённом SSR Teleport ломает гидрацию поддерева,
// поэтому модалку рендерим только на клиенте (после mount).
import { ref as __ref, onMounted as __onMounted } from 'vue';
const __teleportReady = __ref(false);
__onMounted(() => { __teleportReady.value = true; });

import { ref, computed } from 'vue';

const props = defineProps({
  lawyer: Object,
  photos: { type: Array, default: () => [] },
  addresses: { type: Array, default: () => [] },
  currentCityId: { type: Number, default: 0 },
  // Цифры для полосы под шапкой. Они и раньше были на странице, но прятались
  // счётчиками в боковом меню — там их читают как навигацию, а не как заслуги.
  reviewsCount: { type: Number, default: 0 },
  rating: { type: Number, default: 0 },
  practiceCount: { type: Number, default: 0 },
  answersCount: { type: Number, default: 0 },
});

const plural = (n, one, few, many) => {
  const t = Math.abs(n) % 100;
  if (t >= 11 && t <= 14) return many;
  const o = t % 10;
  return o === 1 ? one : (o >= 2 && o <= 4 ? few : many);
};

/**
 * Полоса цифр. Показываем только заполненное: пустая клетка «0 отзывов»
 * работает против юриста сильнее, чем её отсутствие.
 */
const stats = computed(() => {
  const out = [];
  const years = Number(props.lawyer?.expirience) || 0;
  if (years > 1) out.push({ v: years, l: plural(years, 'год в профессии', 'года в профессии', 'лет в профессии') });
  if (props.reviewsCount) out.push({
    v: props.rating ? props.rating.toFixed(1) : props.reviewsCount,
    l: props.rating ? plural(props.reviewsCount, 'отзыв клиента', 'отзыва клиентов', 'отзывов клиентов').replace(/^/, props.reviewsCount + ' ') : 'отзывов',
    star: !!props.rating,
  });
  if (props.practiceCount) out.push({ v: props.practiceCount, l: plural(props.practiceCount, 'дело в практике', 'дела в практике', 'дел в практике') });
  if (props.answersCount) out.push({ v: props.answersCount, l: plural(props.answersCount, 'ответ на вопрос', 'ответа на вопросы', 'ответов на вопросы') });
  return out;
});

const visibleAddresses = computed(() => {
  if (!props.addresses.length) return [];
  const matched = props.addresses.filter(a => a.city_id && a.city_id === props.currentCityId);
  if (matched.length) return matched;
  const generic = props.addresses.filter(a => !a.city_id);
  return generic;
});

// Один телефон: городской адрес → основной
const displayPhone = computed(() => {
  const addrPhone = visibleAddresses.value.find(a => a.phone)?.phone;
  return addrPhone || props.lawyer.phone || null;
});

// Скрываем последние 2 цифры
const maskedPhone = computed(() => {
  if (!displayPhone.value) return null;
  return displayPhone.value.replace(/\d{2}$/, '**');
});

const showPhoneModal = ref(false);

function openPhoneModal() {
  showPhoneModal.value = true;
  try { window.goal('LAWYER_PHONE'); } catch (e) {}
}

const currentSlide = ref(0);

function prevSlide() { if (currentSlide.value > 0) currentSlide.value--; }
function nextSlide() { if (currentSlide.value < props.photos.length - 1) currentSlide.value++; }
function goToSlide(i) { currentSlide.value = i; }
</script>

<template>
  <div class="pt-8 pb-6 w-full text-center lg:text-left" id="about">

    <div class="lg:flex lg:gap-8 lg:items-stretch">

      <!-- Левая колонка: аватар + инфо -->
      <!-- Без фотогалереи колонка занимает ВСЮ ширину: раньше треть шапки
           оставалась пустой просто потому, что галерею не заполнили. -->
      <div class="lg:flex lg:flex-col" :class="photos.length ? 'lg:w-1/2' : 'lg:w-full'">
        <div class="flex flex-col items-center lg:flex-row lg:items-start gap-4 mb-4">
          <img
            itemprop="image"
            loading="eager"
            fetchpriority="high"
            class="rounded-full w-24 h-24 object-cover flex-shrink-0 ring-4 ring-indigo-100 shadow-md"
            :src="'https://nedicom.ru/' + lawyer.avatar_path"
            :alt="'Юрист ' + lawyer.name"
          />
          <div>
            <h1 class="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight lg:text-left mb-1" itemprop="name">
              {{ lawyer.name }}
            </h1>
            <div class="flex flex-wrap justify-center lg:justify-start gap-2 mt-2">
              <span class="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-full px-3 py-1">
                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clip-rule="evenodd"/><path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"/></svg>
                Юрист
              </span>
              <span v-if="lawyer.expirience"
                class="inline-flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-3 py-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                Опыт: {{ lawyer.expirience }}
              </span>
            </div>
          </div>
        </div>

        <p v-if="lawyer.about" class="text-sm text-gray-600 leading-relaxed mb-4" itemprop="description">{{ lawyer.about }}</p>

        <!-- ЦИФРЫ. Занимают ширину, которая раньше пустовала, и отвечают на
             первый вопрос человека: сколько он работает и сколько за ним дел. -->
        <dl v-if="stats.length" class="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-100 rounded-xl overflow-hidden mb-4">
          <div v-for="(s, i) in stats" :key="i" class="bg-white px-4 py-3 text-center lg:text-left">
            <dt class="flex items-baseline justify-center lg:justify-start gap-1 text-xl font-extrabold text-gray-900 tabular-nums">
              <svg v-if="s.star" class="w-4 h-4 text-yellow-400 shrink-0 self-center" fill="currentColor" viewBox="0 0 22 20" aria-hidden="true">
                <use href="#ic-star" />
              </svg>
              {{ s.v }}
            </dt>
            <dd class="mt-0.5 text-xs text-gray-500 leading-snug">{{ s.l }}</dd>
          </div>
        </dl>

        <div v-if="displayPhone" class="mb-2 lg:hidden">
          <button
            type="button"
            @click="openPhoneModal"
            class="phone-lawyer-btn inline-flex items-center gap-3 py-3 px-5 text-white
                   bg-brand-600 hover:bg-brand-700 rounded-xl shadow-lg
                   hover:scale-105 transition-all duration-300 cursor-pointer"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            <div class="flex flex-col items-start">
              <span class="text-base font-bold whitespace-nowrap leading-tight">{{ maskedPhone }}</span>
              <span class="text-xs whitespace-nowrap">Показать номер</span>
            </div>
          </button>
        </div>

        <meta itemprop="telephone" :content="displayPhone ?? lawyer.phone" />
        <meta itemprop="priceRange" content="1000р. - 2000р." />

        <div v-if="visibleAddresses.length || (!addresses.length && (lawyer.address || lawyer.city || lawyer.cities))" class="mt-3 lg:mt-auto lg:pt-3 space-y-1.5">
          <template v-if="visibleAddresses.length">
            <div
              v-for="addr in visibleAddresses"
              :key="addr.id"
              itemprop="address" itemscope itemtype="https://schema.org/PostalAddress"
              class="flex items-start gap-2 text-sm text-gray-500"
            >
              <svg class="w-4 h-4 mt-0.5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <span><span v-if="addr.postal_code" class="text-gray-500">{{ addr.postal_code }}, </span><span itemprop="streetAddress">{{ addr.address }}</span></span>
              <meta v-if="addr.city" itemprop="addressLocality" :content="addr.city.title" />
              <meta itemprop="addressCountry" content="RU" />
            </div>
          </template>
          <template v-else-if="!addresses.length && (lawyer.address || lawyer.city || lawyer.cities)">
            <div itemprop="address" itemscope itemtype="https://schema.org/PostalAddress"
              class="flex items-start gap-2 text-sm text-gray-500">
              <svg class="w-4 h-4 mt-0.5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <span>
                <span v-if="lawyer.address" itemprop="streetAddress">{{ lawyer.address }}</span><span v-if="lawyer.address && (lawyer.city || lawyer.cities)">, </span>
                <span v-if="lawyer.city" itemprop="addressLocality">{{ lawyer.city }}</span>
                <span v-if="lawyer.cities">
                  <span v-if="lawyer.cities.region" itemprop="addressRegion">, {{ lawyer.cities.region }}</span>
                  <span v-if="lawyer.cities.postalcode" itemprop="postalCode">, {{ lawyer.cities.postalcode }}</span>
                  <meta itemprop="addressCountry" content="RU" />
                </span>
              </span>
            </div>
          </template>
        </div>
      </div>

      <!-- Правая колонка: карусель фото (только если есть фото) -->
      <div v-if="photos.length" class="lg:w-1/2 mt-6 lg:mt-0">

        <!-- Основное фото -->
        <div class="relative rounded-2xl overflow-hidden aspect-[5/3]">

          <!-- Мобиль: горизонтальный слайд -->
          <div
            class="flex lg:hidden transition-transform duration-300 ease-in-out h-full"
            :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
          >
            <div v-for="photo in photos" :key="photo.id" class="flex-shrink-0 w-full h-full">
              <img
                class="w-full h-full object-cover block"
                :src="'https://nedicom.ru/' + photo.file_path"
                :alt="lawyer.name"
                loading="lazy"
                draggable="false"
              />
            </div>
          </div>

          <!-- Десктоп: fade -->
          <div class="hidden lg:block h-full">
            <img
              v-for="(photo, idx) in photos"
              :key="photo.id"
              class="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
              :class="currentSlide === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'"
              :src="'https://nedicom.ru/' + photo.file_path"
              :alt="lawyer.name"
              loading="lazy"
              draggable="false"
            />
          </div>

          <!-- Стрелки -->
          <template v-if="photos.length > 1">
            <button
              v-if="currentSlide > 0"
              type="button"
              @click="prevSlide"
              class="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-20 bg-white/75 backdrop-blur-sm shadow text-slate-800 hover:bg-white transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <button
              v-if="currentSlide < photos.length - 1"
              type="button"
              @click="nextSlide"
              class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-20 bg-white/75 backdrop-blur-sm shadow text-slate-800 hover:bg-white transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </template>
        </div>

        <!-- Миниатюры -->
        <div v-if="photos.length > 1" class="flex gap-2 mt-2 overflow-x-auto pb-1">
          <button
            v-for="(photo, idx) in photos"
            :key="photo.id"
            type="button"
            @click="goToSlide(idx)"
            class="flex-shrink-0 w-14 h-9 rounded-lg overflow-hidden transition-all duration-200"
            :class="currentSlide === idx ? 'ring-2 ring-indigo-500 opacity-100' : 'opacity-40 hover:opacity-70'"
          >
            <img :src="'https://nedicom.ru/' + photo.file_path" class="w-full h-full object-cover" :alt="lawyer.name" />
          </button>
        </div>

      </div>
    </div>

  </div>

  <!-- Модалка с телефоном -->
  <Teleport to="body" v-if="__teleportReady">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="showPhoneModal"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
        @click.self="showPhoneModal = false"
      >
        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="opacity-0 translate-y-4"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-4"
        >
          <div v-if="showPhoneModal"
            class="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
          >
            <div class="relative bg-gradient-to-b from-indigo-50 to-white pt-8 pb-4 px-6 flex flex-col items-center gap-3">
              <button
                class="absolute top-4 right-4 text-gray-500 hover:text-gray-600 transition-colors"
                @click="showPhoneModal = false"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>

              <img
                :src="'https://nedicom.ru/' + lawyer.avatar_path"
                :alt="lawyer.name"
                class="w-20 h-20 rounded-full object-cover ring-4 ring-white shadow-lg"
              />
              <p class="text-lg font-bold text-gray-900 text-center">{{ lawyer.name }}</p>
            </div>

            <div class="px-6 pb-6 flex flex-col gap-3">
              <p class="text-sm text-gray-500 text-center leading-relaxed">
                Первичная консультация — бесплатно.<br/>
                Работаем ежедневно, звоните в удобное время.
              </p>

              <a
                :href="'tel:' + displayPhone"
                onclick="window.goal('LAWYER_PHONE_CALL');"
                class="phone-lawyer-btn inline-flex w-full items-center justify-center gap-3 py-3 px-5 text-white
                       bg-brand-600 hover:bg-brand-700 rounded-xl shadow-lg
                       hover:scale-105 transition-all duration-300"
              >
                <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <span class="text-base font-bold whitespace-nowrap">{{ displayPhone }}</span>
              </a>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
@keyframes pulse-ring {
  0%, 100% { box-shadow: 0 0 0 0 rgba(96, 165, 250, 0.6); }
  50%       { box-shadow: 0 0 0 10px rgba(96, 165, 250, 0); }
}
.phone-lawyer-btn {
  animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  box-shadow: 0 4px 20px rgba(79, 70, 229, 0.4);
}
</style>
